/**
 * Fonts: fetching them, and handing out a Shaper per face.
 *
 * A face is expensive - Cairo is 586 KB as a variable TTF, and building a
 * Shaper compiles it into HarfBuzz - so each one is fetched once, shared, and
 * never released. Two things follow from that:
 *
 *   - Loading is async and the canvas has to cope with a face that has not
 *     arrived. It draws nothing for that layer rather than the wrong font,
 *     because a customer who sees Arial for a moment will assume the card
 *     prints in Arial.
 *   - The full TTF is what we outline with at press. A subsetted WOFF2 would
 *     be smaller on screen but cannot be outlined for glyphs it dropped, so
 *     the editor uses the same file the press does. §18 flags this as the
 *     first thing to revisit if the budget gets tight.
 */

// Type-only, so nothing here pulls HarfBuzz into the entry chunk. The module
// is imported dynamically below: harfbuzzjs uses top-level await, and a static
// import would make the whole editor wait on 176 KB of WASM before painting
// anything. §18 and spike/arabic/FINDINGS.md both call this out.
import type { Shaper } from './shaper';

export interface FaceKey {
  family: string;
  weight: number;
  style: 'normal' | 'italic';
}

interface Registered extends FaceKey {
  /** Where the file is. Set by PHP, so the shop can add fonts. */
  url: string;
}

const key = (face: FaceKey) => `${face.family}|${face.weight}|${face.style}`;

/** What the shop offers, filled in at boot from the page's config. */
const registry = new Map<string, Registered>();

/** Faces already compiled. */
const ready = new Map<string, Shaper>();

/** Faces on their way, so two layers in one font do not fetch it twice. */
const loading = new Map<string, Promise<Shaper | null>>();

/** Anyone who wants to redraw when a face arrives. */
const listeners = new Set<() => void>();

export function registerFonts(faces: Registered[]): void {
  for (const face of faces) registry.set(key(face), face);
}

export function availableFonts(): Registered[] {
  return Array.from(registry.values());
}

/** The families on offer, for the font menu. */
export function fontFamilies(): string[] {
  return Array.from(new Set(availableFonts().map((face) => face.family))).sort();
}

/**
 * What a family actually stocks.
 *
 * The Bold and Italic buttons ask this, because a family with no bold face
 * would otherwise offer a button that silently falls back to Regular - the
 * customer presses it, nothing changes, and they conclude the tool is broken.
 * Better to grey it out and say why.
 */
export function familyHas( family: string ): { bold: boolean; italic: boolean } {
  const faces = availableFonts().filter( ( face ) => face.family === family );

  return {
    bold: faces.some( ( face ) => face.weight >= 600 ),
    italic: faces.some( ( face ) => face.style === 'italic' ),
  };
}

/**
 * The closest registered face to what a layer asks for.
 *
 * A layer can ask for Cairo Bold Italic when the shop only stocks Cairo
 * Regular and Bold. Falling back within the family is right; falling back to a
 * different family is not, because it changes the design without saying so.
 */
function resolve(want: FaceKey): Registered | null {
  const exact = registry.get(key(want));

  if (exact) return exact;

  const family = availableFonts().filter((face) => face.family === want.family);

  if (family.length === 0) return null;

  const sameStyle = family.filter((face) => face.style === want.style);
  const pool = sameStyle.length ? sameStyle : family;

  return pool.reduce((best, face) =>
    Math.abs(face.weight - want.weight) < Math.abs(best.weight - want.weight) ? face : best
  );
}

/** A compiled face, or null while it is still on its way. */
export function shaperFor(want: FaceKey): Shaper | null {
  const face = resolve(want);

  if (!face) return null;

  const id = key(face);
  const shaper = ready.get(id);

  if (shaper) return shaper;

  if (!loading.has(id)) loading.set(id, load(face, id));

  return null;
}

async function load(face: Registered, id: string): Promise<Shaper | null> {
  try {
    const response = await fetch(face.url);

    if (!response.ok) throw new Error(`${response.status} fetching ${face.family}`);

    const { Shaper } = await import('./shaper');
    const shaper = new Shaper(await response.arrayBuffer());

    ready.set(id, shaper);
    for (const listener of listeners) listener();

    return shaper;
  } catch (error) {
    // A missing font is not a reason to lose the design. It is reported, the
    // layer stays unrendered, and preflight will refuse the order later.
    console.error('[trimbox] font failed to load', face, error);

    return null;
  } finally {
    loading.delete(id);
  }
}

/** Waits for a face, for code that cannot redraw - export, mainly. */
export async function loadFace(want: FaceKey): Promise<Shaper | null> {
  const face = resolve(want);

  if (!face) return null;

  const id = key(face);

  return ready.get(id) ?? (await (loading.get(id) ?? load(face, id)));
}

export function isReady(want: FaceKey): boolean {
  const face = resolve(want);

  return face ? ready.has(key(face)) : false;
}

/** Every face a document needs, so they can be fetched together. */
export function preload(faces: FaceKey[]): Promise<Array<Shaper | null>> {
  return Promise.all(faces.map(loadFace));
}

export function onFontsChanged(listener: () => void): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}
